// ─────────────────────────────────────────────────────────────
// Transit Analysis Service
// Compares current sky positions with a natal chart to find
// all active transits, classified by duration and impact.
// ─────────────────────────────────────────────────────────────

import type { Planet, PlanetPosition } from './ephemeris';
import { PLANET_NAMES_PT, PLANET_GLYPHS, calculatePlanetPositions, longitudeToHouse, solarHouseCusp, getMoonPhase } from './ephemeris';
import type { AspectDefinition } from './aspects';
import { calculateAspect, calculateIntensity } from './aspects';
import type { TransitWindow } from './transitDuration';
import { calculateTransitWindow } from './transitDuration';

// ─── Types ──────────────────────────────────────────────────

export interface NatalChartData {
  planets: Record<string, { sign: string; degree: number; house: number; longitude?: number; retrograde?: boolean }>;
  mc_sign: string | null;
  mc_degree: number | null;
  dominant_element: string | null;
  dominant_modality: string | null;
  dominant_sign: string | null;
  birth_date: string;
  birth_time: string | null;
  birth_city: string | null;
}

export interface ActiveTransit {
  transitPlanet: Planet;
  natalPoint: string;       // natal planet name or 'Ascendente'/'MC'
  aspect: AspectDefinition;
  natalHouse: number;
  transitHouse: number;
  transitLongitude: number;
  natalLongitude: number;
  currentOrb: number;
  intensity: number;        // 0–100
  window: TransitWindow;
  transitIsRetrograde: boolean;
}

// ─── Zodiac sign → longitude reconstruction ─────────────────

const SIGN_TO_INDEX: Record<string, number> = {
  'Áries': 0, 'Touro': 1, 'Gêmeos': 2, 'Câncer': 3,
  'Leão': 4, 'Virgem': 5, 'Libra': 6, 'Escorpião': 7,
  'Sagitário': 8, 'Capricórnio': 9, 'Aquário': 10, 'Peixes': 11,
};

function reconstructLongitude(sign: string, degree: number): number {
  const idx = SIGN_TO_INDEX[sign];
  if (idx === undefined) return 0;
  return idx * 30 + degree;
}

// ─── English planet key → natal point name ──────────────────

const PLANET_KEY_TO_PT: Record<string, string> = {
  sun: 'Sol', moon: 'Lua', mercury: 'Mercúrio', venus: 'Vênus', mars: 'Marte',
  jupiter: 'Júpiter', saturn: 'Saturno', uranus: 'Urano', neptune: 'Netuno', pluto: 'Plutão',
  mc: 'MC',
};

// ─── Theme map for transit interpretations ──────────────────

export const TRANSIT_THEMES: Record<string, { areas: string[]; theme: string }> = {
  'Jupiter_Conjunção_Sol':       { areas: ['autoconfiança', 'propósito', 'visibilidade'], theme: 'expansão de identidade e oportunidades' },
  'Jupiter_Trígono_Sol':         { areas: ['sorte', 'confiança', 'criatividade'], theme: 'fluxo favorável de energia vital' },
  'Jupiter_Quadratura_Sol':      { areas: ['excessos', 'crescimento forçado'], theme: 'expansão com necessidade de limites' },
  'Jupiter_Conjunção_Lua':       { areas: ['emoções', 'família', 'abundância'], theme: 'expansão emocional' },
  'Jupiter_Trígono_Vênus':       { areas: ['amor', 'finanças', 'prazer'], theme: 'período de graça nos relacionamentos' },
  'Saturn_Conjunção_Sol':        { areas: ['responsabilidade', 'estrutura', 'maturidade'], theme: 'consolidação e prova de maturidade' },
  'Saturn_Quadratura_Sol':       { areas: ['desafios', 'obstáculos', 'aprendizado'], theme: 'pressão para crescimento real' },
  'Saturn_Quadratura_Lua':       { areas: ['emoções', 'família', 'segurança'], theme: 'reestruturação emocional' },
  'Saturn_Oposição_Lua':         { areas: ['relacionamentos', 'necessidades emocionais'], theme: 'tensão entre autonomia e dependência' },
  'Uranus_Conjunção_Sol':        { areas: ['mudanças', 'liberdade', 'identidade'], theme: 'ruptura e reinvenção pessoal' },
  'Uranus_Quadratura_Sol':       { areas: ['instabilidade', 'revolução'], theme: 'crise de identidade gerando transformação' },
  'Neptune_Conjunção_Sol':       { areas: ['espiritualidade', 'ilusões', 'criatividade'], theme: 'dissolução de ego e busca de sentido' },
  'Neptune_Quadratura_Lua':      { areas: ['confusão emocional', 'intuição'], theme: 'sensibilidade e busca interior' },
  'Pluto_Conjunção_Sol':         { areas: ['poder', 'transformação', 'renascimento'], theme: 'transformação profunda de identidade' },
  'Pluto_Quadratura_Sol':        { areas: ['controle', 'poder', 'crises'], theme: 'crise de poder e transformação forçada' },
  'Pluto_Trígono_Sol':           { areas: ['poder pessoal', 'transformação suave'], theme: 'transformação profunda com fluxo favorável' },
  'Venus_Conjunção_Vênus':       { areas: ['amor', 'autoestima', 'beleza'], theme: 'retorno de Vênus, renovação afetiva' },
  'Mars_Conjunção_Marte':        { areas: ['energia', 'ação', 'assertividade'], theme: 'retorno de Marte, renovação de energia vital' },
};

export function getTransitTheme(
  transitPlanet: string,
  aspectNamePt: string,
  natalPoint: string,
): { areas: string[]; theme: string } {
  const key = `${transitPlanet}_${aspectNamePt}_${natalPoint}`;
  return TRANSIT_THEMES[key] ?? {
    areas: ['vida geral'],
    theme: `influência de ${PLANET_NAMES_PT[transitPlanet as Planet] ?? transitPlanet} sobre ${natalPoint} natal`,
  };
}

// ─── Full transit analysis ──────────────────────────────────

export interface TransitAnalysisResult {
  allTransits: ActiveTransit[];
  shortTerm: ActiveTransit[];   // < 30 days active
  mediumTerm: ActiveTransit[];  // 30–179 days active
  longTerm: ActiveTransit[];    // 180+ days total window
  currentSky: PlanetPosition[];
  moonPhase: { name: string; namePt: string; illumination: number };
  retrogradePlanets: Planet[];
}

/**
 * Calculate all active transits between the current sky and a natal chart.
 */
export function analyzeTransits(
  natalChart: NatalChartData,
  date: Date = new Date(),
): TransitAnalysisResult {
  const currentSky = calculatePlanetPositions(date);

  // Derive natal solar house cusp so transit houses are consistent
  const natalSun    = natalChart.planets['sun'];
  const natalSunLon = natalSun ? reconstructLongitude(natalSun.sign, natalSun.degree) : 0;
  const natalCusp0  = solarHouseCusp(natalSunLon);

  // Build natal points with solar-house numbers
  const natalPoints: Array<{ key: string; namePt: string; longitude: number; house: number }> = [];

  for (const [key, data] of Object.entries(natalChart.planets)) {
    if (key === 'ascendant') continue;
    const namePt    = PLANET_KEY_TO_PT[key] ?? key;
    const longitude = data.longitude ?? reconstructLongitude(data.sign, data.degree);
    const house     = longitudeToHouse(longitude, natalCusp0);
    natalPoints.push({ key, namePt, longitude, house });
  }

  if (natalChart.mc_sign) {
    const mcLon  = reconstructLongitude(natalChart.mc_sign, natalChart.mc_degree ?? 0);
    const mcHouse = longitudeToHouse(mcLon, natalCusp0);
    natalPoints.push({ key: 'mc', namePt: 'MC', longitude: mcLon, house: mcHouse });
  }

  // Find all active aspects between transiting planets and natal points
  const allTransits: ActiveTransit[] = [];

  for (const transit of currentSky) {
    for (const natal of natalPoints) {
      // Skip same planet transit-to-natal for fast-moving bodies (Sun, Moon)
      // when the planet key matches, these are "returns" and treated separately
      const found = calculateAspect(transit.longitude, natal.longitude);
      if (!found.aspect) continue;

      const intensity = calculateIntensity(found.orb, found.aspect.orb);

      // Calculate transit window
      const window = calculateTransitWindow(
        transit.planet,
        natal.longitude,
        found.aspect.angle,
        date,
        transit.longitude,
        transit.isRetrograde,
        found.aspect.orb,
      );

      // Determine which house the transit planet is in (natal solar house system)
      const transitHouse = longitudeToHouse(transit.longitude, natalCusp0);

      allTransits.push({
        transitPlanet: transit.planet,
        natalPoint: natal.namePt,
        aspect: found.aspect,
        natalHouse: natal.house,
        transitHouse,
        transitLongitude: transit.longitude,
        natalLongitude: natal.longitude,
        currentOrb: found.orb,
        intensity,
        window,
        transitIsRetrograde: transit.isRetrograde,
      });
    }
  }

  // Sort by intensity (strongest first)
  allTransits.sort((a, b) => b.intensity - a.intensity);

  // Classify by duration
  const shortTerm = allTransits.filter(t => !t.window.isLongTerm && t.window.totalWindowDays < 30);
  const mediumTerm = allTransits.filter(t => t.window.isLongTerm && t.window.totalWindowDays < 180);
  const longTerm = allTransits.filter(t => t.window.totalWindowDays >= 180);

  // Retrograde planets
  const retrogradePlanets = currentSky
    .filter(p => p.isRetrograde)
    .map(p => p.planet);

  const moonPhaseInfo = getMoonPhase(date);

  return {
    allTransits,
    shortTerm,
    mediumTerm,
    longTerm,
    currentSky,
    moonPhase: {
      name: moonPhaseInfo.name,
      namePt: moonPhaseInfo.namePt,
      illumination: moonPhaseInfo.illumination,
    },
    retrogradePlanets,
  };
}

// ─── Format transit for display ─────────────────────────────

export function formatTransitLabel(t: ActiveTransit): string {
  const glyph = PLANET_GLYPHS[t.transitPlanet] ?? '';
  const name = PLANET_NAMES_PT[t.transitPlanet] ?? t.transitPlanet;
  return `${glyph} ${name} ${t.aspect.symbol} ${t.aspect.namePt} ${t.natalPoint} natal`;
}
